import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { FamilyMessage, MessageThreadDetail } from '../../../../lib/api';
import { formatDateToJapanese } from '../../../../utils/dateFormat';
import { LockIcon, CheckCircleIcon, ArrowIcon } from '../../../../components/Icons/Icons';

interface MessageListProps {
  threads: MessageThreadDetail[];
  patientId: number | null;
  /** スレッドを選んで返信する。選んだ `thread_id` が送信リクエストに載る（B-8 #5）。 */
  onReplyToThread: (threadId: string) => void;
  /** 返信できるか。退所などで送信が閉じている利用者ではボタンを出さない */
  canReply: boolean;
  /**
   * 通知から開いたときに位置を合わせる対象。
   *
   * **最新ではなく通知の対象**に合わせる（B-10 #5）。見つからなければ何もしない（B-10 #3）。
   * `message_id` は `thread_id` との組でしか一意にならないため（Living Spec §6）、
   * **両方そろって初めて対象を特定できる**。
   */
  focusThreadId?: string | null;
  focusMessageId?: number | null;
  /**
   * 位置合わせに成功したときに呼ぶ。呼び出し側は対象を外すこと（URL から消す）。
   * 外さないと、一覧の更新やタブの切り替えのたびに通知の対象へ引き戻される。
   */
  onFocusComplete?: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

// --- 共通カードベース ---
const CardBase = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

// --- 非表示設定カード ---
const HiddenCard = styled(CardBase)`
  border: 2px solid ${({ theme }) => theme.colors.border.supplementary};
`;

const HiddenHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.backgroundSupplementary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

// 連絡帳コメントの引用ブロック。上下左右の余白はここで一括して持つ
const ReplyQuoteContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.ms};
`;

const CommentNavSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }
`;

const CommentDate = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CommentNavLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ChevronRight = styled.span`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
`;

const ReplyContentBox = styled.div`
  margin-top: 8px;
  padding: ${({ theme }) => theme.spacing.ms};
  background: ${({ theme }) => theme.colors.backgroundTableHeader};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

/** 引用・メッセージ・返信ボタンの区切り線。カード内の区切りはすべてこれに揃える */
const Divider = styled.hr`
  margin: 0 ${({ theme }) => theme.spacing.ms};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.driver};
`;

const MessageBodySection = styled.div`
  padding: ${({ theme }) => theme.spacing.ms};
`;

// 送信者名（左）とタイムスタンプ（右）を同じ行に並べる
// 文字サイズが異なる（16px / 14px）ため、中央揃えでは行ボックスの中心が揃うだけで
// ベースラインが 1.5px ずれる。文字の揃いはベースラインで見えるため baseline で揃える
const MessageHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: 4px;
`;

const SenderName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
  /* 長い送信者名でタイムスタンプを押し出さないよう、名前側を縮退させて省略表示する */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MessageContent = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MessageFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Timestamp = styled.span`
  flex-shrink: 0;
  /* 送信者名が無い場合も右上に配置する（space-between だけでは左寄せになる） */
  margin-left: auto;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 9999px;
  background: ${({ theme }) => theme.colors.backgroundTableGood};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1.5;
`;


// --- 本人表示カード ---
const VisibleCard = styled(CardBase)``;

// --- スレッドへの返信ボタン ---
const ReplyActionSection = styled.div`
  padding: ${({ theme }) => theme.spacing.ms};
`;

const ReplyActionButton = styled.button`
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
`;

// --- ヘルパー関数 ---

const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${formatDateToJapanese(date)} ${hours}:${minutes}`;
};

const formatSourceDateLabel = (dateString: string): string => {
  const date = new Date(dateString);
  return formatDateToJapanese(date);
};

/**
 * 「スタッフ確認済」バッジを出すか。
 *
 * 事業所からの返信（`incoming`）には**付けない**。確認状態は家族→事業所のメッセージだけが
 * 持つため（staff-reply.md B-8 #4 / B-6 #6）。
 */
const getStatusDisplay = (message: FamilyMessage) => {
  return message.direction !== 'incoming' && !!message.confirmed_at;
};

/**
 * 送信者として表示する名前。
 *
 * 事業所からの返信は事業所名ではなく固定の「デイサービスより」を出し、スタッフ個人名も出さない
 * （staff-reply.md B-8 #2 / D-3、Figma の表記）。
 * 返信レコードは `sender_name` を持たないので、ここでしか名前を決められない。
 */
const getSenderLabel = (message: FamilyMessage): string | null => {
  if (message.direction === 'incoming') return 'デイサービスより';
  return message.sender_name || null;
};

// --- 共通サブコンポーネント ---

const ReplyQuoteSection: React.FC<{
  replyTo: FamilyMessage['reply_to'];
  onCommentClick: (sourceDate: string) => void;
}> = ({ replyTo, onCommentClick }) => {
  if (!replyTo) return null;

  return (
    <>
      <ReplyQuoteContainer>
        <CommentNavSection onClick={() => onCommentClick(replyTo.source_date)}>
          <CommentDate>{formatSourceDateLabel(replyTo.source_date)}</CommentDate>
          <CommentNavLabel>連絡帳のコメント</CommentNavLabel>
          <ChevronRight><ArrowIcon /></ChevronRight>
        </CommentNavSection>
        <ReplyContentBox>{replyTo.source_content}</ReplyContentBox>
      </ReplyQuoteContainer>
      <Divider />
    </>
  );
};

const MessageBody: React.FC<{
  message: FamilyMessage;
  /** `message_id` は `thread_id` との組でしか一意にならないため、位置合わせ用に両方持たせる */
  threadId: string;
}> = ({ message, threadId }) => (
  <MessageBodySection data-thread-id={threadId} data-message-id={message.message_id}>
    <MessageHeader>
      {getSenderLabel(message) && (
        <SenderName>{getSenderLabel(message)}</SenderName>
      )}
      <Timestamp>{formatTimestamp(message.created_at)}</Timestamp>
    </MessageHeader>
    <MessageContent>{message.content}</MessageContent>
    {getStatusDisplay(message) && (
      <MessageFooter>
        <StatusBadge>
          <CheckCircleIcon />
          スタッフ確認済
        </StatusBadge>
      </MessageFooter>
    )}
  </MessageBodySection>
);

const ReplyAction: React.FC<{
  thread: MessageThreadDetail;
  canReply: boolean;
  onReplyToThread: (threadId: string) => void;
}> = ({ thread, canReply, onReplyToThread }) => {
  // 送信が閉じている利用者では出さない。出すと押せるのに入力画面へ進めず、
  // 「押しても何も起きない」状態になる（「新しく連絡する」タブと同じ扱い）
  if (!canReply) return null;
  return (
  <>
    <Divider />
    <ReplyActionSection>
      <ReplyActionButton type="button" onClick={() => onReplyToThread(thread.thread_id)}>
        返信する
      </ReplyActionButton>
    </ReplyActionSection>
  </>
  );
};

/** スレッド内のメッセージ列。メッセージの間には必ず区切り線を入れる */
const ThreadMessages: React.FC<{
  thread: MessageThreadDetail;
  onCommentClick: (sourceDate: string) => void;
}> = ({ thread, onCommentClick }) => (
  <>
    {thread.messages.map((message, index) => (
      <React.Fragment key={message.message_id}>
        {index > 0 && <Divider />}
        <ReplyQuoteSection replyTo={message.reply_to} onCommentClick={onCommentClick} />
        <MessageBody message={message} threadId={thread.thread_id} />
      </React.Fragment>
    ))}
  </>
);

// --- カードコンポーネント ---

const HiddenMessageCard: React.FC<{
  thread: MessageThreadDetail;
  onCommentClick: (sourceDate: string) => void;
  onReplyToThread: (threadId: string) => void;
  canReply: boolean;
}> = ({ thread, onCommentClick, onReplyToThread, canReply }) => (
  <HiddenCard>
    <HiddenHeader>
      <LockIcon />
      <span>非表示設定（利用者本人には見えません）</span>
    </HiddenHeader>
    <ThreadMessages thread={thread} onCommentClick={onCommentClick} />
    <ReplyAction thread={thread} canReply={canReply} onReplyToThread={onReplyToThread} />
  </HiddenCard>
);

const VisibleMessageCard: React.FC<{
  thread: MessageThreadDetail;
  onCommentClick: (sourceDate: string) => void;
  onReplyToThread: (threadId: string) => void;
  canReply: boolean;
}> = ({ thread, onCommentClick, onReplyToThread, canReply }) => (
  <VisibleCard>
    <ThreadMessages thread={thread} onCommentClick={onCommentClick} />
    <ReplyAction thread={thread} canReply={canReply} onReplyToThread={onReplyToThread} />
  </VisibleCard>
);

export const MessageList: React.FC<MessageListProps> = ({
  threads,
  patientId,
  onReplyToThread,
  canReply,
  focusThreadId,
  focusMessageId,
  onFocusComplete,
}) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  // 通知から開いたとき、対象のメッセージをファーストビューに入れる（B-10 #1 / #5）。
  // 対象が見つからないときは何もしない — 通常どおり開くのが仕様（B-10 #3）
  useEffect(() => {
    if (focusThreadId == null || focusMessageId == null) return;

    // threads を依存に持つのは、データ到着前に描画されたときに取り逃さないため。
    // 合わせたあとは onFocusComplete で対象が外れるので、一覧の更新（60 秒ごと /
    // 確認済みの 15 秒ポーリング）やタブの切り替えで引き戻されることはない

    // message_id は thread_id との組でしか一意にならない（Living Spec §6）。
    // 片方だけで探すと、同じ ID を持つ別スレッドの発言へ飛ぶ
    const target = containerRef.current?.querySelector(
      `[data-thread-id="${CSS.escape(focusThreadId)}"][data-message-id="${focusMessageId}"]`
    );
    if (!target) return;

    target.scrollIntoView({ block: 'start' });
    onFocusComplete?.();
  }, [focusThreadId, focusMessageId, threads, onFocusComplete]);

  const getLatestMessageAt = (thread: MessageThreadDetail): number => {
    if (thread.messages.length === 0) return 0;
    return Math.max(...thread.messages.map(m => new Date(m.created_at).getTime()));
  };

  const sortedThreads = useMemo(
    () => [...threads].sort((a, b) => getLatestMessageAt(b) - getLatestMessageAt(a)),
    [threads]
  );

  const handleReplyQuoteClick = useCallback(
    (sourceDate: string) => {
      const params = new URLSearchParams();
      params.set('date', sourceDate);
      if (patientId) {
        params.set('patient_id', String(patientId));
      }
      navigate(`/?${params.toString()}`);
    },
    [navigate, patientId]
  );

  return (
    <Container ref={containerRef}>
      {sortedThreads.map((thread) =>
        thread.hide_from_patient ? (
          <HiddenMessageCard
            key={thread.thread_id}
            thread={thread}
            onCommentClick={handleReplyQuoteClick}
            onReplyToThread={onReplyToThread}
            canReply={canReply}
          />
        ) : (
          <VisibleMessageCard
            key={thread.thread_id}
            thread={thread}
            onCommentClick={handleReplyQuoteClick}
            onReplyToThread={onReplyToThread}
            canReply={canReply}
          />
        )
      )}
    </Container>
  );
};

export default MessageList;
